import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { ShieldAlert, Search, Loader2, RotateCw, UserCog } from "lucide-react";

// SuperAdmin — "who impersonated whom" audit table.
// Filters by actor email, target email, time window. Not writable — every
// row is created server-side when an impersonation session is minted.
export default function SuperAdminImpersonations() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actorEmail, setActorEmail] = useState("");
  const [targetEmail, setTargetEmail] = useState("");
  const [sinceDays, setSinceDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/audit/impersonations", {
        params: {
          actor_email: actorEmail || undefined,
          target_email: targetEmail || undefined,
          since_days: sinceDays,
          limit: 500,
        },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load impersonation audit");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="ll-card-flat overflow-hidden" data-testid="sa-imp-audit-card">
      <div className="px-5 py-3 border-b flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--ll-border)" }}>
        <div className="flex items-center gap-2">
          <ShieldAlert size={14} />
          <div className="font-display font-bold">Impersonation audit</div>
          <div className="text-xs text-[var(--ll-ink-soft)]">{total} in last {sinceDays}d</div>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ll-ink-muted)]" />
            <input
              value={actorEmail}
              onChange={(e) => setActorEmail(e.target.value)}
              placeholder="Actor email"
              data-testid="sa-imp-filter-actor"
              className="pl-7 pr-3 py-1.5 border text-xs w-40"
              style={{ borderColor: "var(--ll-border)" }}
            />
          </div>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ll-ink-muted)]" />
            <input
              value={targetEmail}
              onChange={(e) => setTargetEmail(e.target.value)}
              placeholder="Target email"
              data-testid="sa-imp-filter-target"
              className="pl-7 pr-3 py-1.5 border text-xs w-40"
              style={{ borderColor: "var(--ll-border)" }}
            />
          </div>
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(parseInt(e.target.value, 10))}
            data-testid="sa-imp-filter-range"
            className="px-2 py-1.5 border text-xs bg-white"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </select>
          <button type="submit" data-testid="sa-imp-refresh" className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)] flex items-center gap-1" style={{ borderColor: "var(--ll-border)" }}>
            <RotateCw size={11} /> Apply
          </button>
        </form>
      </div>
      {loading ? (
        <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]">
          <Loader2 size={16} className="mx-auto animate-spin mb-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="sa-imp-empty">
          <UserCog size={24} className="mx-auto mb-2 opacity-40" />
          No impersonation sessions in this window.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="sa-imp-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
              <th className="px-4 py-2 font-medium">When</th>
              <th className="px-4 py-2 font-medium">Actor</th>
              <th className="px-4 py-2 font-medium">Target</th>
              <th className="px-4 py-2 font-medium">Tenant</th>
              <th className="px-4 py-2 font-medium">Reason</th>
              <th className="px-4 py-2 font-medium">TTL</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b hover:bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-border)" }}>
                <td className="px-4 py-2 text-xs whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-2 text-xs">{r.actor_email}</td>
                <td className="px-4 py-2 text-xs">
                  <div>{r.target_email}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{r.target_role}</div>
                </td>
                <td className="px-4 py-2 text-xs text-[var(--ll-ink-soft)]">{r.tenant_name}</td>
                <td className="px-4 py-2 text-xs max-w-md">{r.reason}</td>
                <td className="px-4 py-2 text-[11px] text-[var(--ll-ink-soft)]">{r.ttl_minutes}m</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
